import { create } from 'zustand';

import { API, getRequest, patchRequest, postRequest } from '@/api/api.ts';
import type { DockCard } from '@/store/marina-services/marina-service.dto.ts';

export type CreateMarinaBody = {
  max_length: number;
  max_beam: number;
  shallowest_draft: number;
  price_per_foot_daily: string;
  price_per_foot_monthly: string;
};

type MarinaServicesStore = {
  marinas: DockCard[];
  bookedMarinas: any[];
  marina: DockCard | null;
  getAllMarinas: () => Promise<any[]>;
  getBookedMarinas: (vesselId?: string) => Promise<any[]>;
  getMarinaById: (id: string, vesselId: string) => Promise<any>;
  createMarina: (id: string, body: CreateMarinaBody) => Promise<any>;
  setFavorite: (id: string, favorite: boolean, vessel_id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
};

export const useMarinaServicesStore = create<MarinaServicesStore>((set) => ({
  marinas: [],
  marina: null,
  bookedMarinas: [],
  loading: false,
  error: null,
  getAllMarinas: async () => {
    set({ loading: true, error: null });
    try {
      const response = await getRequest(
        API.SERVICE_PROVIDERS,
        'service-provider?type=marina&limit=9999&page=1',
      );
      const data = response?.data as { items?: any[] } | undefined;
      const items = data?.items ?? [];
      set({ marinas: items, loading: false });
      return items;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
      return [];
    }
  },
  getMarinaById: async (id: string, vesselId: string) => {
    set({ loading: true, error: null });
    try {
      const response = await getRequest(
        API.SERVICE_PROVIDERS,
        `service-provider/${id}?vessel_id=${vesselId}`,
      );
      set({ marina: response?.data as DockCard, loading: false });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
      return [];
    }
  },
  createMarina: async (id: string, body: CreateMarinaBody) => {
    try {
      const response = await postRequest(
        API.SERVICE_PROVIDERS,
        `service-provider/${id}/marina`,
        body,
      );
      return response.data;
    } catch (e) {
      console.error(e);
    }
  },
  setFavorite: async (id: string, favorite: boolean, vessel_id: string) => {
    try {
      await patchRequest(API.SERVICE_PROVIDERS, `service-provider/${id}/favorite`, {
        vessel_id,
        favorite,
      });
      set((state) => ({
        marinas: state.marinas.map((marina) =>
          marina.id === id ? { ...marina, favorite } : marina,
        ),
        marina: state.marina?.id === id ? { ...state.marina, favorite } : state.marina,
      }));
      return;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e) });
      return;
    }
  },
  getBookedMarinas: async (vesselId?: string) => {
    set({ loading: true, error: null });
    try {
      const response = await getRequest(
        API.VESSEL,
        'marina?limit=100&page=1',
        false,
        undefined,
        vesselId,
      );
      const data = response?.data as { items?: any[] } | undefined;
      const items = data?.items ?? [];
      set({ bookedMarinas: items, loading: false });
      return items;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
      return [];
    }
  },
}));
